import {useEffect} from "react";

const EMPTY_RESTORE = () => {};

function resolveImageMimeType(imageUrl) {
    if (!imageUrl) {
        return "";
    }

    const normalizedUrl = String(imageUrl)
        .split(/[?#]/)[0]
        .toLowerCase();

    if (normalizedUrl.endsWith(".webp")) {
        return "image/webp";
    }

    if (normalizedUrl.endsWith(".png")) {
        return "image/png";
    }

    if (
        normalizedUrl.endsWith(".jpg") ||
        normalizedUrl.endsWith(".jpeg")
    ) {
        return "image/jpeg";
    }

    if (normalizedUrl.endsWith(".gif")) {
        return "image/gif";
    }

    if (normalizedUrl.endsWith(".svg")) {
        return "image/svg+xml";
    }

    return "";
}

function updateMeta(
    attribute,
    key,
    content
) {
    if (!content) {
        return EMPTY_RESTORE;
    }

    const selector =
        `meta[${attribute}="${key}"]`;

    let element =
        document.head.querySelector(
            selector
        );

    const wasCreated = !element;

    const previousContent = element
        ? element.getAttribute("content")
        : null;

    if (!element) {
        element =
            document.createElement(
                "meta"
            );

        element.setAttribute(
            attribute,
            key
        );

        document.head.appendChild(
            element
        );
    }

    element.setAttribute(
        "content",
        content
    );

    return () => {
        if (wasCreated) {
            element.remove();
            return;
        }

        if (previousContent === null) {
            element.removeAttribute(
                "content"
            );

            return;
        }

        element.setAttribute(
            "content",
            previousContent
        );
    };
}

function updateCanonical(
    canonicalUrl
) {
    if (!canonicalUrl) {
        return EMPTY_RESTORE;
    }

    let element =
        document.head.querySelector(
            'link[rel="canonical"]'
        );

    const wasCreated = !element;

    const previousHref = element
        ? element.getAttribute("href")
        : null;

    if (!element) {
        element =
            document.createElement(
                "link"
            );

        element.setAttribute(
            "rel",
            "canonical"
        );

        document.head.appendChild(
            element
        );
    }

    element.setAttribute(
        "href",
        canonicalUrl
    );

    return () => {
        if (wasCreated) {
            element.remove();
            return;
        }

        if (previousHref === null) {
            element.removeAttribute(
                "href"
            );

            return;
        }

        element.setAttribute(
            "href",
            previousHref
        );
    };
}

function updateStructuredData(value) {
    if (!value) {
        return EMPTY_RESTORE;
    }

    const elementId =
        "page-structured-data";

    let element =
        document.getElementById(
            elementId
        );

    const wasCreated = !element;

    const previousValue = element
        ? element.textContent
        : "";

    if (!element) {
        element =
            document.createElement(
                "script"
            );

        element.id = elementId;
        element.type =
            "application/ld+json";

        document.head.appendChild(
            element
        );
    }

    element.textContent = value;

    return () => {
        if (wasCreated) {
            element.remove();
            return;
        }

        element.textContent =
            previousValue;
    };
}

export default function PageSeo({
    title,
    description,
    imageUrl,
    imageAlt,
    canonicalUrl,
    type = "website",
    author,
    publishedTime,
    modifiedTime,
    structuredData,
}) {
    const structuredDataValue =
        structuredData
            ? JSON.stringify(
                structuredData
            )
            : "";

    const imageMimeType =
        resolveImageMimeType(
            imageUrl
        );

    const twitterCardType =
        imageUrl
            ? "summary_large_image"
            : "summary";

    useEffect(() => {
        const previousTitle =
            document.title;

        document.title = title;

        const restoreHandlers = [
            updateMeta(
                "name",
                "description",
                description
            ),
            updateMeta(
                "name",
                "robots",
                "index, follow"
            ),
            updateMeta(
                "name",
                "author",
                author
            ),
            updateMeta(
                "property",
                "og:title",
                title
            ),
            updateMeta(
                "property",
                "og:description",
                description
            ),
            updateMeta(
                "property",
                "og:url",
                canonicalUrl
            ),
            updateMeta(
                "property",
                "og:type",
                type
            ),
            updateMeta(
                "property",
                "og:site_name",
                "Светофор Логистика"
            ),
            updateMeta(
                "property",
                "og:locale",
                "ru_RU"
            ),
            updateMeta(
                "property",
                "og:image",
                imageUrl
            ),
            updateMeta(
                "property",
                "og:image:secure_url",
                imageUrl
            ),
            updateMeta(
                "property",
                "og:image:type",
                imageMimeType
            ),
            updateMeta(
                "property",
                "og:image:alt",
                imageAlt
            ),
            updateMeta(
                "name",
                "twitter:card",
                twitterCardType
            ),
            updateMeta(
                "name",
                "twitter:title",
                title
            ),
            updateMeta(
                "name",
                "twitter:description",
                description
            ),
            updateMeta(
                "name",
                "twitter:image",
                imageUrl
            ),
            updateMeta(
                "name",
                "twitter:image:alt",
                imageAlt
            ),
            updateMeta(
                "property",
                "article:published_time",
                publishedTime
            ),
            updateMeta(
                "property",
                "article:modified_time",
                modifiedTime
            ),
            updateCanonical(
                canonicalUrl
            ),
            updateStructuredData(
                structuredDataValue
            ),
        ];

        return () => {
            document.title =
                previousTitle;

            restoreHandlers
                .reverse()
                .forEach(
                    (restore) =>
                        restore()
                );
        };
    }, [
        title,
        description,
        imageUrl,
        imageMimeType,
        imageAlt,
        canonicalUrl,
        type,
        author,
        publishedTime,
        modifiedTime,
        structuredDataValue,
        twitterCardType,
    ]);

    return null;
}
